/**
 * MiraWidget — AI Feedback & Support drawer for ABS Contract Admin
 * Powered by Mira by MARFI (https://marfi.ai), running on Gemini 3 Flash.
 *
 * Features:
 *  - Floating bottom-right bubble with circle-cropped MARFI logo.
 *  - Initial intent chips (Bug / Feature / Question) + per-turn quick replies
 *    suggested by Gemini.
 *  - Auto-captures: screenshot (html2canvas), telemetry (browser/viewport/URL),
 *    logs (console errors + failed fetches via ring buffer). No manual attach.
 *  - State (session, messages, screenshot, telemetry) persists across page
 *    changes via localStorage.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, AlertTriangle, Loader2, Camera, ExternalLink, Bug, Sparkles, HelpCircle } from "lucide-react";
import html2canvas from "html2canvas";
import { installMiraTelemetry, collectTelemetry, getRecentLogs } from "./miraTelemetry";
import { useAuth } from "../auth/AuthContext";

const MARFI_LOGO = `${process.env.PUBLIC_URL || ""}/marfi-logo.jpeg`;
const STORAGE_KEY_BASE = "mira_widget_state_v2";

// Per-user storage key so shared browsers don't leak User A's draft to User B.
// Falls back to a generic key when called before the user email is known.
function storageKey(userEmail) {
  if (!userEmail) return STORAGE_KEY_BASE;
  return `${STORAGE_KEY_BASE}:${userEmail.toLowerCase()}`;
}

// --------------------------- Small UI atoms ---------------------------

const Pill = ({ children, tone = "neutral" }) => {
  const tones = {
    bug: "bg-rose-100 text-rose-700 border-rose-200",
    feature: "bg-emerald-100 text-emerald-700 border-emerald-200",
    question: "bg-sky-100 text-sky-700 border-sky-200",
    p0: "bg-red-600 text-white border-red-700",
    p1: "bg-amber-500 text-white border-amber-600",
    p2: "bg-gray-400 text-white border-gray-500",
    critical: "bg-red-600 text-white border-red-700",
    high: "bg-orange-500 text-white border-orange-600",
    medium: "bg-amber-400 text-amber-900 border-amber-500",
    low: "bg-emerald-400 text-emerald-900 border-emerald-500",
    neutral: "bg-gray-100 text-gray-700 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${tones[tone] || tones.neutral}`}>
      {children}
    </span>
  );
};

const MiraAvatar = ({ size = 32 }) => (
  <div
    className="rounded-full overflow-hidden ring-2 ring-white shadow-sm flex-shrink-0 bg-white"
    style={{ width: size, height: size }}
    data-testid="mira-avatar"
  >
    <img src={MARFI_LOGO} alt="Mira" className="w-full h-full object-cover" />
  </div>
);

const ChipRow = ({ chips, onPick, disabled, testIdPrefix = "mira-chip" }) => {
  if (!chips || chips.length === 0) return null;
  return (
    <div className="mt-2 ml-8 flex flex-wrap gap-1.5" data-testid="mira-chip-row">
      {chips.map((label, i) => (
        <button
          key={`${label}-${i}`}
          onClick={() => onPick(label)}
          disabled={disabled}
          className="text-[12px] px-2.5 py-1 rounded-full border border-[hsl(var(--brand-blue))]/30 bg-white text-[hsl(var(--brand-blue))] hover:bg-[hsl(var(--brand-blue))] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
          data-testid={`${testIdPrefix}-${i}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const IntentChips = ({ onPick, disabled }) => {
  const intents = [
    { id: "bug", label: "Something's broken", icon: Bug, tone: "rose" },
    { id: "feature", label: "Feature idea", icon: Sparkles, tone: "emerald" },
    { id: "question", label: "Question", icon: HelpCircle, tone: "sky" },
  ];
  const tones = {
    rose: "border-rose-200 hover:bg-rose-50 text-rose-700",
    emerald: "border-emerald-200 hover:bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 hover:bg-sky-50 text-sky-700",
  };
  return (
    <div className="mt-2 ml-8 grid grid-cols-1 gap-1.5" data-testid="mira-intent-chips">
      {intents.map(({ id, label, icon: Icon, tone }) => (
        <button
          key={id}
          onClick={() => onPick(id, label)}
          disabled={disabled}
          className={`text-[12px] font-medium px-3 py-2 rounded-lg border bg-white inline-flex items-center gap-2 transition-colors shadow-sm disabled:opacity-40 ${tones[tone]}`}
          data-testid={`mira-intent-${id}`}
        >
          <Icon size={13} />
          {label}
        </button>
      ))}
    </div>
  );
};

// --------------------------- Persistence helpers ---------------------------

function loadPersisted(userEmail) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userEmail));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity: drop sessions older than 24h
    if (parsed?.updatedAt && Date.now() - parsed.updatedAt > 24 * 60 * 60 * 1000) {
      window.localStorage.removeItem(storageKey(userEmail));
      return null;
    }
    // Don't restore a stuck "capturing" state — downgrade so the UI doesn't
    // look like it's mid-capture forever.
    if (parsed?.screenshotStatus === "capturing") {
      parsed.screenshotStatus = parsed.screenshotId ? "saved" : "idle";
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePersisted(userEmail, state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(userEmail),
      JSON.stringify({ ...state, updatedAt: Date.now() })
    );
  } catch {
    // quota or disabled — silently ignore
  }
}

function clearPersisted(userEmail) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(userEmail));
    // Also clean up the legacy un-scoped key if it exists
    window.localStorage.removeItem(STORAGE_KEY_BASE);
  } catch {
    /* */
  }
}

// =====================================================================

export default function MiraWidget({ enabled = true }) {
  const { user } = useAuth();
  const userEmail = user?.email || null;

  // Hydrate from localStorage so the widget persists across route changes / refresh.
  // Scoped per user email so a shared browser doesn't leak User A's draft to B.
  const persisted = useRef(loadPersisted(userEmail)).current;

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(persisted?.sessionId || null);
  // messages: [{role, content, chips?: string[]}]
  const [messages, setMessages] = useState(persisted?.messages || []);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(persisted?.summary || null);
  const [submitState, setSubmitState] = useState({ status: "idle", result: null, error: null });
  const [screenshotId, setScreenshotId] = useState(persisted?.screenshotId || null);
  const [screenshotStatus, setScreenshotStatus] = useState(persisted?.screenshotStatus || "idle");
  const [showIntentChips, setShowIntentChips] = useState(
    persisted?.sessionId ? false : true
  );

  const drawerRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Install telemetry / log capture exactly once
  useEffect(() => {
    installMiraTelemetry();
  }, []);

  // If the user logs out (or switches accounts), wipe in-memory draft so we
  // don't blow away the new user's view with someone else's session.
  const prevEmailRef = useRef(userEmail);
  useEffect(() => {
    if (prevEmailRef.current && prevEmailRef.current !== userEmail) {
      // user changed — start fresh
      setSessionId(null);
      setMessages([]);
      setSummary(null);
      setScreenshotId(null);
      setScreenshotStatus("idle");
      setShowIntentChips(true);
      setOpen(false);
    }
    prevEmailRef.current = userEmail;
  }, [userEmail]);

  // Persist state on every change (per user)
  useEffect(() => {
    if (!sessionId && messages.length === 0) return;
    savePersisted(userEmail, { sessionId, messages, summary, screenshotId, screenshotStatus });
  }, [userEmail, sessionId, messages, summary, screenshotId, screenshotStatus]);

  // Auto-scroll on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, summary, submitState]);

  // -------- Screenshot capture (silent) ----------
  const captureScreenshot = useCallback(async (sid) => {
    if (!sid) return;
    setScreenshotStatus("capturing");
    const drawerEl = drawerRef.current;
    const prevDisplay = drawerEl ? drawerEl.style.display : null;
    if (drawerEl) drawerEl.style.display = "none";

    try {
      await new Promise((r) => setTimeout(r, 120));
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        backgroundColor: "#ffffff",
        ignoreElements: (el) => el.dataset && el.dataset.miraIgnore === "true",
      });
      const dataUrl = canvas.toDataURL("image/png", 0.85);
      if (drawerEl) drawerEl.style.display = prevDisplay || "";

      const res = await fetch("/api/feedback/capture-screen", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          image_base64: dataUrl,
          page_url: window.location.href,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setScreenshotId(data.screenshot_id);
        setScreenshotStatus("saved");
      } else {
        setScreenshotStatus("failed");
      }
    } catch (err) {
      console.warn("Mira screenshot capture failed:", err);
      if (drawerEl) drawerEl.style.display = prevDisplay || "";
      setScreenshotStatus("failed");
    }
  }, []);

  // -------- Open widget ----------
  const openWidget = async () => {
    setOpen(true);
    setSubmitState({ status: "idle", result: null, error: null });

    if (sessionId) {
      // Resume existing session
      setTimeout(() => inputRef.current?.focus(), 200);
      return;
    }

    // Brand-new session: show opener + intent chips
    setBusy(true);
    try {
      const res = await fetch("/api/feedback/start", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page_url: window.location.href }),
      });
      if (!res.ok) throw new Error(`Mira failed to start (${res.status})`);
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", content: data.reply, chips: data.chips || [] }]);
      setShowIntentChips(true);
    } catch (err) {
      setMessages([
        { role: "assistant", content: "I'm having trouble connecting — try again in a moment." },
      ]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  };

  const closeWidget = () => setOpen(false);

  // -------- Pick initial intent ----------
  const pickIntent = async (intentId, intentLabel) => {
    setShowIntentChips(false);

    // If we already have an opener session, just continue it via /chat with
    // the intent label — that's what the user "said". Reuses the same
    // session_id so we don't orphan a row in feedback_sessions and the
    // backend transcript matches the UI verbatim.
    if (sessionId) {
      await sendText(intentLabel);
      // Capture the screenshot once we know the user is reporting something
      captureScreenshot(sessionId);
      return;
    }

    // No session yet (edge case if the opener fetch failed) — seed via /start
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: intentLabel }]);
    try {
      const res = await fetch("/api/feedback/start", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intent: intentId, page_url: window.location.href }),
      });
      if (!res.ok) throw new Error(`Start failed (${res.status})`);
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply, chips: data.chips || [] },
      ]);
      if (data.ready && data.summary) setSummary(data.summary);
      captureScreenshot(data.session_id);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I lost connection. Mind trying again?" },
      ]);
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  };

  // -------- Send a typed message OR pick a chip ----------
  const sendText = async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || busy) return;

    if (!sessionId) {
      // No session yet — treat as a free-form first message
      setBusy(true);
      setMessages((m) => [...m, { role: "user", content: trimmed }]);
      try {
        const res = await fetch("/api/feedback/start", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ initial_message: trimmed, page_url: window.location.href }),
        });
        if (!res.ok) throw new Error(`Start failed (${res.status})`);
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages((m) => [
          ...m,
          { role: "assistant", content: data.reply, chips: data.chips || [] },
        ]);
        if (data.ready && data.summary) setSummary(data.summary);
        setShowIntentChips(false);
        captureScreenshot(data.session_id);
      } catch (err) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: "Sorry — I lost connection. Mind trying again?" },
        ]);
      } finally {
        setBusy(false);
      }
      return;
    }

    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setBusy(true);
    try {
      const res = await fetch("/api/feedback/chat", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!res.ok) throw new Error(`Chat error ${res.status}`);
      const data = await res.json();
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply, chips: data.chips || [] },
      ]);
      if (data.ready && data.summary) setSummary(data.summary);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry — I lost connection. Mind trying that again?" },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const sendInput = async () => {
    const t = input.trim();
    if (!t) return;
    setInput("");
    await sendText(t);
  };

  const pickChip = (chipLabel) => sendText(chipLabel);

  // -------- Submit / file the ticket ----------
  const submitTicket = async () => {
    if (!summary || !sessionId) return;
    setSubmitState({ status: "submitting", result: null, error: null });
    try {
      const res = await fetch("/api/feedback/submit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          triage: summary,
          page_url: window.location.href,
          telemetry: collectTelemetry(),
          logs: getRecentLogs(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.detail || "Submit failed");
      setSubmitState({ status: "success", result: data, error: null });
      // Successful submit clears persisted state so the next open is fresh
      clearPersisted(userEmail);
    } catch (err) {
      setSubmitState({ status: "error", result: null, error: err.message || "Submit failed" });
    }
  };

  const resetReport = () => {
    setSessionId(null);
    setMessages([]);
    setInput("");
    setSummary(null);
    setSubmitState({ status: "idle", result: null, error: null });
    setScreenshotId(null);
    setScreenshotStatus("idle");
    setShowIntentChips(true);
    clearPersisted(userEmail);
    openWidget();
  };

  if (!enabled) return null;

  // The last assistant message's chips are the "live" chips (the ones the user can act on)
  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  })();

  return (
    <div data-mira-ignore="true" data-testid="mira-widget-root">
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="mira-bubble"
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            onClick={openWidget}
            className="fixed bottom-5 right-5 z-[9998] group flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full shadow-lg hover:shadow-xl bg-[hsl(var(--brand-navy))] text-white border border-white/10 transition-all"
            title="Talk to Mira"
            data-testid="mira-open-btn"
          >
            <MiraAvatar size={32} />
            <span className="text-xs font-semibold tracking-wide whitespace-nowrap pr-1">
              Help &amp; feedback
            </span>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full ring-2 ring-[hsl(var(--brand-navy))]" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mira-drawer"
            ref={drawerRef}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="fixed bottom-5 right-5 z-[9999] w-[380px] max-w-[calc(100vw-2.5rem)] h-[560px] max-h-[calc(100vh-2.5rem)] rounded-2xl shadow-2xl flex flex-col overflow-hidden bg-white border border-gray-200"
            data-testid="mira-drawer"
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-[hsl(var(--brand-navy))] text-white">
              <MiraAvatar size={36} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm leading-tight">Mira</div>
                <a
                  href="https://marfi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-white/70 hover:text-white inline-flex items-center gap-1 transition-colors"
                  data-testid="mira-marfi-link"
                >
                  by MARFI <ExternalLink size={9} />
                </a>
              </div>
              <button
                onClick={closeWidget}
                className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
                title="Close"
                data-testid="mira-close-btn"
              >
                <X size={16} />
              </button>
            </div>

            {/* Status strip */}
            <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1.5">
                <Camera size={11} />
                {screenshotStatus === "capturing" && <span>Capturing screen…</span>}
                {screenshotStatus === "saved" && <span className="text-emerald-600">Screen attached</span>}
                {screenshotStatus === "failed" && <span className="text-amber-600">Screen unavailable</span>}
                {screenshotStatus === "idle" && <span>Standing by</span>}
              </span>
              <span className="text-gray-400">Gemini 3 Flash</span>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-3 py-4 bg-gray-50"
              data-testid="mira-message-list"
            >
              {messages.map((m, i) => {
                const isLastAssistant = i === lastAssistantIdx;
                const isOpener = i === 0 && messages.length === 1;
                const intentChipsHere = m.role === "assistant" && isOpener && showIntentChips;
                const showChips =
                  m.role === "assistant" &&
                  isLastAssistant &&
                  !intentChipsHere && // don't duplicate the intent picker
                  m.chips?.length > 0 &&
                  !summary &&
                  submitState.status !== "success";
                return (
                  <div key={i} className="mb-3">
                    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} items-end gap-2`}>
                      {m.role === "assistant" && <MiraAvatar size={24} />}
                      <div
                        className={`max-w-[78%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed shadow-sm ${
                          m.role === "user"
                            ? "bg-[hsl(var(--brand-blue))] text-white rounded-br-sm"
                            : "bg-white text-gray-800 border border-gray-100 rounded-bl-sm"
                        }`}
                        data-testid={`mira-msg-${m.role}-${i}`}
                      >
                        {m.content}
                      </div>
                    </div>
                    {intentChipsHere && (
                      <IntentChips onPick={pickIntent} disabled={busy} />
                    )}
                    {showChips && (
                      <ChipRow chips={m.chips} onPick={pickChip} disabled={busy} />
                    )}
                  </div>
                );
              })}

              {busy && (
                <div className="flex items-end gap-2 mb-3">
                  <MiraAvatar size={24} />
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 text-[13px] text-gray-400 inline-flex items-center gap-2 shadow-sm">
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                  </div>
                </div>
              )}

              {/* Triage Confirm card */}
              {summary && submitState.status !== "success" && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 rounded-xl border border-[hsl(var(--brand-blue))]/40 bg-white shadow-md p-3"
                  data-testid="mira-summary-card"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Pill tone={summary.type}>{summary.type}</Pill>
                    <Pill tone={summary.severity}>{summary.severity}</Pill>
                    {(summary.labels || [])
                      .filter((l) => l === "p0" || l === "p1" || l === "p2")
                      .slice(0, 1)
                      .map((p) => (
                        <Pill key={p} tone={p}>
                          {p.toUpperCase()}
                        </Pill>
                      ))}
                  </div>
                  <div className="text-sm font-semibold text-gray-900 leading-snug mb-1.5">
                    {summary.title}
                  </div>
                  <div className="text-[12px] text-gray-600 leading-relaxed mb-2 line-clamp-3">
                    {summary.description}
                  </div>
                  {submitState.status === "error" && (
                    <div className="mb-2 text-[11px] text-rose-600 flex items-center gap-1">
                      <AlertTriangle size={11} /> {submitState.error}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={submitTicket}
                      disabled={submitState.status === "submitting"}
                      className="flex-1 text-xs font-semibold bg-[hsl(var(--brand-blue))] hover:bg-blue-700 text-white px-3 py-2 rounded-md transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
                      data-testid="mira-submit-btn"
                    >
                      {submitState.status === "submitting" ? (
                        <>
                          <Loader2 size={12} className="animate-spin" /> Filing…
                        </>
                      ) : (
                        <>File this report</>
                      )}
                    </button>
                    <button
                      onClick={() => setSummary(null)}
                      className="text-xs text-gray-500 hover:text-gray-800 px-2 py-2 rounded-md"
                      data-testid="mira-cancel-summary-btn"
                    >
                      Not yet
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Success */}
              {submitState.status === "success" && submitState.result && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3"
                  data-testid="mira-success-card"
                >
                  <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm mb-1.5">
                    <CheckCircle2 size={16} /> Filed — thank you!
                  </div>
                  <div className="text-[12px] text-emerald-800 leading-relaxed mb-2">
                    {submitState.result.discord_ok !== false && (
                      <>The team has been notified on Discord.</>
                    )}
                    {submitState.result.github?.url ? (
                      <>
                        {" "}Tracking on{" "}
                        <a
                          href={submitState.result.github.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline font-medium inline-flex items-center gap-0.5"
                          data-testid="mira-github-link"
                        >
                          GitHub #{submitState.result.github.number}
                          <ExternalLink size={10} />
                        </a>
                        .
                      </>
                    ) : (
                      <>
                        {" "}Your report was saved, but we couldn&apos;t open a GitHub ticket
                        automatically — the team will follow up directly.
                      </>
                    )}
                  </div>
                  {submitState.result.discord_ok === false && (
                    <div
                      className="mb-2 text-[11px] text-amber-700 flex items-center gap-1"
                      data-testid="mira-discord-warning"
                    >
                      <AlertTriangle size={11} /> Discord notification didn&apos;t go through — we saved your report regardless.
                    </div>
                  )}
                  <button
                    onClick={resetReport}
                    className="text-xs font-medium text-emerald-700 hover:text-emerald-900 underline"
                    data-testid="mira-new-report-btn"
                  >
                    Send another →
                  </button>
                </motion.div>
              )}
            </div>

            {/* Input bar */}
            <div className="border-t border-gray-100 bg-white p-2.5">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 96) + "px";
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendInput();
                    }
                  }}
                  placeholder={
                    submitState.status === "success"
                      ? "Report sent — start a new one above."
                      : "Type a message or tap an option…"
                  }
                  disabled={busy || submitState.status === "submitting" || submitState.status === "success"}
                  className="flex-1 resize-none text-[13px] px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-blue))]/30 focus:border-[hsl(var(--brand-blue))]/40 bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                  style={{ maxHeight: 96 }}
                  data-testid="mira-input"
                />
                <button
                  onClick={sendInput}
                  disabled={!input.trim() || busy || submitState.status === "success"}
                  className="bg-[hsl(var(--brand-blue))] hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                  title="Send"
                  data-testid="mira-send-btn"
                >
                  <Send size={14} />
                </button>
              </div>
              <div className="text-[10px] text-gray-400 mt-1.5 text-center">
                Mira by{" "}
                <a
                  href="https://marfi.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-600"
                >
                  MARFI
                </a>{" "}
                · Powered by Gemini 3 Flash
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
